import { Button, IconButton, Tooltip } from "@mui/material";
import { RefreshData } from "components/helper";
import DeleteModal from "components/Modals/DeleteModal";
import useKothar from "context/useKothar";
import DOMPurify from "dompurify";
import { MaterialReactTable, useMaterialReactTable } from "material-react-table";
import { useEffect, useMemo, useState } from "react";
import { AiFillDelete, AiFillEdit } from "react-icons/ai";
import { FaPlusCircle } from "react-icons/fa";
import { Link } from "react-router-dom";
import { hasPermission } from "utils/permissionUtils";
import { highlightHtml, stripHtml } from "utils/utils";
import AddCheckList from "./AddCheckList";
import ViewCheckListModal from "./ViewCheckListModal";
import { deleteData } from "const/axios";
import { getFileIcon } from "utils/utils";
import { P } from "const/permissions";

const CheckList = ({ color = "light", readOnly = false, embedded = false }) => {
  const [openConfirmationModal, setOpenConfirmationModal] = useState({});
  const [{ checkList, token, isCheckListLoading }, { refetchCheckList }] = useKothar();
  const [openCheckListDrawer, setOpenCheckListDrawer] = useState({
    state: false,
    id: null,
    data: null,
  });
  const [openViewModal, setOpenViewModal] = useState({
    state: false,
    data: null,
  });
  const canManage = !readOnly && hasPermission(P.CHECKLIST.CREATE);

  useEffect(() => {
    if (!checkList?.length) {
      refetchCheckList();
    }
  }, []);

  const handleDeleteData = () => {
    deleteData(
      `check-list/${openConfirmationModal?.id}`,
      () => {
        setOpenConfirmationModal({ state: false, id: null });
        refetchCheckList();
      },
      token
    );
  };

  const columns = useMemo(
    () => [
      {
        accessorKey: "key1",
        header: "Title",
        size: 150,
        Cell: ({ renderedCellValue }) => (
          <Tooltip title={renderedCellValue}>
            <div className="flex items-center">
              <div className=" mr-2">{renderedCellValue}</div>
            </div>
          </Tooltip>
        ),
      },

      {
        accessorKey: "key3",
        header: "Mandatory Requirements",
        size: 150,
        filterFn: (row, columnId, filterValue) => {
          const text = stripHtml(row.getValue(columnId));
          return text.toLowerCase().includes(filterValue.toLowerCase());
        },

        Cell: ({ cell, table }) => {
          const rawHtml = cell.getValue() ?? "";

          // Get MRT global search value
          const searchValue = table.getState().globalFilter;

          const highlightedHtml = highlightHtml(rawHtml, searchValue);

          return (
            <div
              dangerouslySetInnerHTML={{
                __html: DOMPurify.sanitize(highlightedHtml),
              }}
              className="max-w-[200px] truncate"
            />
          );
        },
      },
      {
        accessorKey: "key5",
        header: "Optional Requirements",
        size: 100,
        filterFn: (row, columnId, filterValue) => {
          const text = stripHtml(row.getValue(columnId));
          return text.toLowerCase().includes(filterValue.toLowerCase());
        },

        Cell: ({ cell, table }) => {
          const rawHtml = cell.getValue() ?? "";

          // Get MRT global search value
          const searchValue = table.getState().globalFilter;

          const highlightedHtml = highlightHtml(rawHtml, searchValue);

          return (
            <div
              dangerouslySetInnerHTML={{
                __html: DOMPurify.sanitize(highlightedHtml),
              }}
              className="max-w-[200px] truncate"
            />
          );
        },
      },

      {
        accessorKey: "arrKey1",
        header: "Attachments",
        size: 150,
        Cell: ({ renderedCellValue }) => (
          <div className="flex flex-wrap gap-2">
            {renderedCellValue?.length > 0
              ? renderedCellValue?.map((item, index) => (
                  <Tooltip key={item || index} title={item} arrow>
                    <a
                      href={item}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center justify-center w-10 h-10 rounded border border-app-border hover:bg-app-surface-hover transition-colors"
                    >
                      {getFileIcon(item)}
                    </a>
                  </Tooltip>
                ))
              : "-"}
          </div>
        ),
      },

      {
        accessorKey: "actions",
        header: "Actions",
        size: 150,
        Cell: ({ row }) => {
          if (!canManage) return null;
          return (
            <div className="flex items-center">
              <Tooltip title="Edit Check List" arrow>
                <IconButton
                  onClick={() =>
                    setOpenCheckListDrawer({
                      state: true,
                      id: row?.original?.id,
                      data: row?.original,
                    })
                  }
                >
                  <AiFillEdit className="text-primary cursor-pointer" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Delete Check List" arrow>
                <IconButton
                  onClick={() =>
                    setOpenConfirmationModal({
                      state: true,
                      id: row?.original?.id,
                    })
                  }
                >
                  <AiFillDelete className="text-red-600 cursor-pointer" />
                </IconButton>
              </Tooltip>
            </div>
          );
        },
      },
    ],
    [canManage]
  );

  const table = useMaterialReactTable({
    columns: readOnly ? columns.filter((col) => col.accessorKey !== "actions") : columns,
    data: checkList,
    rowNumberDisplayMode: "original",
    enableRowNumbers: true,
    initialState: {
      enableGlobalFilter: true,
      showGlobalFilter: true,
      density: "compact",
      pagination: {
        pageSize: 20,
        pageIndex: 0,
      },
    },
    enablePagination: true,
    enableStickyHeader: true,
    muiTableContainerProps: {
      sx: {
        maxHeight: "70vh", // Set max height
        minHeight: "70vh", // Set max height
        overflowY: "auto", // Enable vertical scrolling
      },
    },
    state: {
      isLoading: isCheckListLoading, //cell skeletons and loading overlay
      showProgressBars: isCheckListLoading, //progress bars while refetching
      isSaving: isCheckListLoading, //progress bars and save button spinners
    },
    muiTableBodyRowProps: ({ row }) => ({
      onClick: (event) => {
        const isActionClick =
          event.target.closest("a[href]") ||
          event.target.closest("img[src]") ||
          event.target.closest("button[type='button']");
        if (!isActionClick) {
          setOpenViewModal({
            state: true,
            data: row?.original,
          });
        }
      },
      sx: {
        cursor: "pointer",
        "&:hover": {
          backgroundColor: "rgba(0, 0, 0, 0.04)",
        },
      },
    }),
  });

  return (
    <div className={embedded ? "w-full" : "flex flex-wrap mt-4 dashBody"}>
      <div className={embedded ? "w-full" : "w-full mb-12 px-4"}>
        <div
          className={
            "relative flex flex-col min-w-0 break-words w-full mb-6 shadow-lg rounded " +
            "bg-app-surface text-app-text"
          }
        >
          <div className="rounded-t mb-0 px-4 py-3 border-0">
            <div className="flex flex-wrap items-center justify-between">
              <h3 className={"font-semibold text-lg " + "text-app-text"}>
                Check List ({checkList?.length}) <RefreshData refreshData={refetchCheckList} />
              </h3>

              {canManage && (
                <Button
                  variant="contained"
                  startIcon={<FaPlusCircle />}
                  component={Link}
                  onClick={() =>
                    setOpenCheckListDrawer({
                      state: true,
                      id: null,
                      data: null,
                    })
                  }
                >
                  Add Check List
                </Button>
              )}
            </div>
          </div>
          <div className="block w-full overflow-x-auto">
            <MaterialReactTable table={table} />
          </div>
        </div>
      </div>
      {!readOnly && openConfirmationModal.state && (
        <DeleteModal
          open={openConfirmationModal.state}
          item="Check List"
          handleCancel={() => setOpenConfirmationModal({ state: false, id: null })}
          handleDelete={() => handleDeleteData()}
        />
      )}
      {!readOnly && (
        <AddCheckList
          open={openCheckListDrawer}
          handleClose={() =>
            setOpenCheckListDrawer({
              state: false,
              id: null,
              data: null,
            })
          }
          setOpen={setOpenCheckListDrawer}
          refetchCheckList={refetchCheckList}
        />
      )}
      <ViewCheckListModal
        open={openViewModal.state}
        handleClose={() =>
          setOpenViewModal({
            state: false,
            data: null,
          })
        }
        data={openViewModal.data}
        onEdit={
          openViewModal.data && canManage
            ? () => {
                setOpenViewModal({ state: false, data: null });
                setOpenCheckListDrawer({
                  state: true,
                  id: openViewModal.data?.id,
                  data: openViewModal.data,
                });
              }
            : null
        }
      />
    </div>
  );
};

export default CheckList;
